import {
  FieldSuggestion,
  OptimizableFieldType,
  FIELD_LABEL,
  FIELD_WEIGHT
} from './ai-optimize-constants';
import { AuditedProduct } from './scoring-engine-constants';
import { ProjectedRewrites, projectRewrittenScore } from './scoring-engine-utils';

const PROPOSALS_STORAGE_KEY = 'pdp-scorecard-proposals-v1';

/** A single AI-generated suggestion with the user's accept/reject choice preserved. */
export interface SavedSuggestion extends FieldSuggestion {
  accepted: boolean;
}

/** Per-product container of AI-generated rewrites, stored separately from the scored products. */
export interface ProductProposal {
  productId: string;
  generatedAt: number;
  title?: SavedSuggestion;
  bullets?: SavedSuggestion;
  description?: SavedSuggestion;
  alexaScore?: number;
}

export type ProposalStore = Record<string, ProductProposal>;

const isSavedSuggestion = (v: unknown): v is SavedSuggestion =>
  !!v &&
  typeof v === 'object' &&
  typeof (v as Record<string, unknown>).suggestion === 'string' &&
  typeof (v as Record<string, unknown>).reasoning === 'string' &&
  typeof (v as Record<string, unknown>).accepted === 'boolean';

const isProductProposal = (v: unknown): v is ProductProposal => {
  if (!v || typeof v !== 'object') return false;
  const p = v as Record<string, unknown>;
  if (typeof p.productId !== 'string' || typeof p.generatedAt !== 'number') return false;
  if (p.title !== undefined && !isSavedSuggestion(p.title)) return false;
  if (p.bullets !== undefined && !isSavedSuggestion(p.bullets)) return false;
  if (p.description !== undefined && !isSavedSuggestion(p.description)) return false;
  if (p.alexaScore !== undefined && typeof p.alexaScore !== 'number') return false;
  return true;
};

/** Parses a persisted store, dropping any entries that don't match the expected shape
 * (e.g. from a stale schema version or hand-edited localStorage) instead of letting
 * them crash the scoring/render pipeline downstream. */
const parseProposalStore = (parsed: unknown): ProposalStore => {
  if (typeof parsed !== 'object' || parsed === null) return {};
  const next: ProposalStore = {};
  for (const [id, proposal] of Object.entries(parsed as Record<string, unknown>)) {
    if (isProductProposal(proposal)) {
      next[id] = proposal;
    }
  }
  return next;
};

export const readProposalStore = (): ProposalStore => {
  try {
    const raw = localStorage.getItem(PROPOSALS_STORAGE_KEY);
    if (!raw) return {};
    return parseProposalStore(JSON.parse(raw));
  } catch {
    return {};
  }
};

export const writeProposalStore = (store: ProposalStore): void => {
  try {
    localStorage.setItem(PROPOSALS_STORAGE_KEY, JSON.stringify(store));
  } catch {
    // Quota / disabled — non-fatal.
  }
};

export const clearProposalStore = (): void => {
  try {
    localStorage.removeItem(PROPOSALS_STORAGE_KEY);
  } catch {
    // ignore
  }
};

const proposalToRewrites = (proposal: ProductProposal): ProjectedRewrites => {
  const rewrites: ProjectedRewrites = {};
  if (proposal.title?.accepted && proposal.title.suggestion) {
    rewrites.title = proposal.title.suggestion;
  }
  if (proposal.bullets?.accepted && proposal.bullets.suggestion) {
    rewrites.bullets = proposal.bullets.suggestion
      .split(/\n+/)
      .map(b => b.replace(/^\s*[-*\d.)\s]+/, '').trim())
      .filter(b => b !== '')
      .join('|');
  }
  if (proposal.description?.accepted && proposal.description.suggestion) {
    rewrites.description = proposal.description.suggestion;
  }
  return rewrites;
};

/** Returns the projected scored product if the proposal has at least one accepted field, else null. */
export const getProjectedProduct = (
  product: AuditedProduct,
  proposal: ProductProposal | undefined
): AuditedProduct | null => {
  if (!proposal) return null;
  const rewrites = proposalToRewrites(proposal);
  if (Object.keys(rewrites).length === 0) return null;
  return projectRewrittenScore(product, rewrites);
};

/** The product with its accepted AI rewrites applied, falling back to the original when none are accepted. */
export const getEffectiveProduct = (
  product: AuditedProduct,
  proposal: ProductProposal | undefined
): AuditedProduct => getProjectedProduct(product, proposal) ?? product;

export const countAcceptedFields = (proposal: ProductProposal | undefined): number => {
  if (!proposal) return 0;
  let n = 0;
  if (proposal.title?.accepted) {
    n++;
  }
  if (proposal.bullets?.accepted) {
    n++;
  }
  if (proposal.description?.accepted) {
    n++;
  }
  return n;
};

export const pruneEmptyProposals = (store: ProposalStore): ProposalStore => {
  const next: ProposalStore = {};
  for (const [id, proposal] of Object.entries(store)) {
    if (proposal.title || proposal.bullets || proposal.description) {
      next[id] = proposal;
    }
  }
  return next;
};

export interface ProposalSummary {
  productCount: number;
  acceptedFieldCount: number;
  currentAverage: number;
  projectedAverage: number;
  delta: number;
}

export const summarizeProposals = (
  products: AuditedProduct[],
  store: ProposalStore
): ProposalSummary => {
  if (products.length === 0) {
    return {
      productCount: 0,
      acceptedFieldCount: 0,
      currentAverage: 0,
      projectedAverage: 0,
      delta: 0
    };
  }

  let productCount = 0;
  let acceptedFieldCount = 0;
  let currentSum = 0;
  let projectedSum = 0;

  for (const product of products) {
    const proposal = store[product.id];
    currentSum += product.finalScore;
    const projected = getProjectedProduct(product, proposal);
    if (projected) {
      productCount++;
      acceptedFieldCount += countAcceptedFields(proposal);
      projectedSum += projected.finalScore;
    } else {
      projectedSum += product.finalScore;
    }
  }

  const currentAverage = currentSum / products.length;
  const projectedAverage = projectedSum / products.length;
  return {
    productCount,
    acceptedFieldCount,
    currentAverage,
    projectedAverage,
    delta: projectedAverage - currentAverage
  };
};

const escapeCSV = (value: string | number): string => {
  const str = String(value);
  if (/[",\n\r]/.test(str)) {
    return `"${str.replace(/"/g, '""')}"`;
  }
  return str;
};

const getCurrentCopyForField = (product: AuditedProduct, field: OptimizableFieldType): string => {
  if (field === 'title') {
    return product.title === ' — ' ? '' : product.title;
  }
  if (field === 'bullets') {
    return product.featureBullets.join(' | ');
  }
  return product.fullDescription === ' — ' ? '' : product.fullDescription;
};

/** Builds a CSV of every accepted rewrite across the portfolio for the content team. */
export const buildProposalsCSV = (products: AuditedProduct[], store: ProposalStore): string => {
  const header = [
    'ASIN',
    'Brand',
    'Product Title',
    'Field',
    'Field Weight (%)',
    'Current Score',
    'Projected Score',
    'Projected Lift',
    'Current Copy',
    'Proposed Copy',
    'AI Reasoning'
  ];
  const rows: string[][] = [header];

  const fields: OptimizableFieldType[] = ['title', 'bullets', 'description'];
  for (const product of products) {
    const proposal = store[product.id];
    if (!proposal) continue;
    const projected = getProjectedProduct(product, proposal);
    if (!projected) continue;

    for (const field of fields) {
      const suggestion = proposal[field];
      if (!suggestion?.accepted) continue;
      const currentCopy = getCurrentCopyForField(product, field);

      rows.push([
        product.asin,
        product.brand === ' — ' ? '' : product.brand,
        product.title === ' — ' ? '' : product.title,
        FIELD_LABEL[field],
        String(FIELD_WEIGHT[field]),
        String(product.finalScore),
        String(projected.finalScore),
        `+${projected.finalScore - product.finalScore}`,
        currentCopy,
        suggestion.suggestion,
        suggestion.reasoning
      ]);
    }
  }

  return rows.map(r => r.map(escapeCSV).join(',')).join('\n');
};
